import type React from 'react';
import { useEffect, useState } from 'react';
import { ImagePlaceholder } from './ImagePlaceholder';

interface ResilientImageProps
	extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'onError' | 'src'> {
	src: string | null | undefined;
	/**
	 * Fallback rendered when `src` is empty, or after we've already tried
	 * once to refresh and the second attempt also failed.
	 *
	 * Optional: defaults to the shared `<ImagePlaceholder />` (a centered,
	 * theme-aware photo icon). Pass `fallback={null}` to render nothing
	 * (e.g. marketing/promo imagery that should stay blank when absent).
	 */
	fallback?: React.ReactNode;
	/**
	 * Called once when the image fails to load. Typical use: invalidate the
	 * react-query whose payload produced this URL so the caller refetches
	 * a freshly-signed S3 link from the API. We only fire this on the
	 * FIRST failure per `src` value — if the refetched URL also fails,
	 * we fall through to the placeholder instead of looping.
	 *
	 * Why this matters. Item / catalog / promo images are presigned S3 URLs
	 * whose validity is bounded by both their own `X-Amz-Expires` AND the
	 * lifetime of the AWS session that signed them. Production runs under
	 * an IAM role so the session is effectively unbounded; in local dev
	 * (and any environment using static STS credentials) the session can
	 * age out before the URL's own clock, leaving cached URLs that look
	 * fresh server-side but are rejected by S3 with `ExpiredToken`. Treating
	 * an `<img>` load failure as a "stale URL, refetch please" signal lets
	 * the page self-heal without the user having to reload.
	 */
	onLikelyStaleUrl?: () => void;
}

/**
 * `<img>` that treats a load failure as a probable stale-presigned-URL
 * signal: notifies its caller (so the parent query can refetch and produce
 * a freshly-signed URL), then if that doesn't help on the second try,
 * renders the supplied fallback. Resets on `src` change.
 *
 * If `onLikelyStaleUrl` is omitted, the component falls straight to
 * `fallback` on the first error — useful for places that have nothing
 * sensible to refetch.
 */
export function ResilientImage({
	src,
	fallback = <ImagePlaceholder />,
	onLikelyStaleUrl,
	...rest
}: ResilientImageProps): React.JSX.Element {
	const [retried, setRetried] = useState(false);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		setRetried(false);
		setFailed(false);
	}, [src]);

	if (src == null || src === '' || failed) {
		return <>{fallback}</>;
	}

	return (
		<img
			{...rest}
			src={src}
			onError={() => {
				if (!retried && onLikelyStaleUrl != null) {
					setRetried(true);
					onLikelyStaleUrl();
				} else {
					setFailed(true);
				}
			}}
		/>
	);
}
